import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { productionAPI, productsAPI } from '../services/api';
import toast from 'react-hot-toast';

function ProductionForm() {
  // Helper function to get current datetime in the format required for datetime-local input
  const getCurrentDateTime = () => {
    const now = new Date();
    // Format: YYYY-MM-DDTHH:mm
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const [formData, setFormData] = useState({
    variantId: '',
    quantity: '',
    notes: '',
    castDate: getCurrentDateTime()
  });

  const queryClient = useQueryClient();

  const { data: products } = useQuery('products', productsAPI.getAll, {
    select: data => data.data
  });

  const registerMutation = useMutation(productionAPI.registerCasting, {
    onSuccess: (data) => {
      toast.success(data.data.message);
      setFormData({ variantId: '', quantity: '', notes: '', castDate: getCurrentDateTime() });
      queryClient.invalidateQueries(['production', 'stock']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Fejl ved registrering');
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.variantId || !formData.quantity) {
      toast.error('Udfyld alle påkrævede felter');
      return;
    }

    registerMutation.mutate({
      variantId: formData.variantId,
      quantity: parseInt(formData.quantity),
      notes: formData.notes,
      castDate: new Date(formData.castDate).toISOString()
    });
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Registrer Støbning</h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Produktvariant *
            </label>
            <select
              value={formData.variantId}
              onChange={(e) => setFormData({...formData, variantId: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Vælg variant...</option>
              {products?.map(product => 
                product.variants?.map(variant => (
                  <option key={variant.id} value={variant.id}>
                    {product.name} - {variant.sku} ({variant.dimensions.length}x{variant.dimensions.width}x{variant.dimensions.height}cm)
                  </option>
                ))
              )}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Antal *
            </label>
            <input
              type="number"
              value={formData.quantity}
              onChange={(e) => setFormData({...formData, quantity: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              min="1"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Støbedato *
            </label>
            <input
              type="datetime-local"
              value={formData.castDate}
              onChange={(e) => setFormData({...formData, castDate: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Bemærkninger
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows="3"
            />
          </div>

          <button
            type="submit"
            disabled={registerMutation.isLoading}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {registerMutation.isLoading ? 'Registrerer...' : 'Registrer Støbning'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default ProductionForm;